// FileUploadSection.tsx
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  DownloadForOfflineOutlined as DownloadOutlineIcon,
} from "@mui/icons-material";
import {
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import parse from "html-react-parser";
import React, { useEffect, useRef, useState } from "react";
import urls from "../../configmap/config.json";
import { documentclassification } from "../../utils/fieldConstants";
import { colors } from "../../theme/theme";
import Delete from "../../assets/images/Vector.png";
import { useDispatch, useSelector } from "react-redux";
import FileFormat from "../../assets/images/fileFormat.png";
import { CloseOutActions } from "../../redux/closeoutslice/closeoutSlice";
import { Button, ButtonIcon } from "pepsico-ds";
import { getFilePreview } from "../../services/engagementHistoryService";
import { downloadFile } from "../../utils/filechunk";
type documentInput = {
  isedit?: boolean;
  showUploadSection?: boolean;
};

const DocumentUploadInput: React.FC<documentInput> = ({
  isedit = true,
  showUploadSection = true,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const maxFileSizeInMB = urls.maxFileSize / (1024 * 1024);
  const acceptedFileTypes = /\.(doc|docx|pdf|xls|xlsx|ppt|pptx|txt|rtf|json)$/i;
  const message = isedit
    ? urls.disclaimerMsg.replace("{infoSecURL}", urls.infoSecURL)
    : urls.uploadFiledisclaimerMsg.replace("{infoSecURL}", urls.infoSecURL);
  const {
    confidentialChecked,
    restrictedChecked,
    internalChecked,
    confidentialSelection,
    restrictedSelection,
    internalSelection,
    files,
    errorMessage,
    isUploading,
    uploadedFilesSuccess,
    uploadProgress,
    preuploadedFilesSuccess,
  } = useSelector((state: any) => state.closeOut);

  const [downloadUrl, setDownloadUrl] = React.useState<string | null>(null);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set()
  );
  const dispatch = useDispatch();
  const endOfFilesRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const updatedFileClassificationSelection: any = {};
    // Iterate over files to ensure each file has an entry
    files.forEach((file: any) => {
      // If a file is classified, set it accordingly
      if (confidentialSelection[file.name] === documentclassification.Yes) {
        updatedFileClassificationSelection[file.name] =
          documentclassification.confidentialCatergory;
      } else if (
        restrictedSelection[file.name] === documentclassification.Yes
      ) {
        updatedFileClassificationSelection[file.name] =
          documentclassification.restrictedCategory;
      } else if (internalSelection[file.name] === documentclassification.Yes) {
        updatedFileClassificationSelection[file.name] =
          documentclassification.internalCategory;
      } else {
        // If no classification is selected, set as undefined
        updatedFileClassificationSelection[file.name] = undefined;
      }
    });
    // Update the state (dispatch the action to update the store)
    dispatch(
      CloseOutActions.setFileClassification(updatedFileClassificationSelection)
    );
  }, [
    files,
    confidentialSelection,
    restrictedSelection,
    internalSelection,
    confidentialChecked,
    restrictedChecked,
    internalChecked,
    dispatch,
  ]);
  useEffect(() => {
    dispatch(CloseOutActions.resetAll());
  }, [dispatch]);
  useEffect(() => {
    if (preuploadedFilesSuccess.length) {
      dispatch(CloseOutActions.setPreUploadedFilesToIntake());
    }
  }, []);
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };
  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles) {
      const fileArray = Array.from(droppedFiles);
      const dataTransfer = new DataTransfer();
      fileArray.forEach((file) => dataTransfer.items.add(file));

      handleFileUpload({
        target: { files: dataTransfer.files },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const uploadedFiles = Array.from(event.target.files);
      // Reset file input value after usage to allow selecting the same file again
      event.target.value = "";
      const validFiles: File[] = [];
      const exceeded: string[] = [];
      const lesserSized: string[] = [];
      const invalid: string[] = [];
      const duplicate: string[] = [];

      // Create a Set of already uploaded file names for fast duplicate checking
      const uploadedFileNames = new Set(files.map((file: any) => file.name));

      uploadedFiles.forEach((file) => {
        // const isFileExist = files.some((existingFile: any) => existingFile.name === file.name);
        const isFileExist = uploadedFileNames.has(file.name);

        if (isFileExist) {
          duplicate.push(file.name);
        } else if (!acceptedFileTypes.test(file.name)) {
          invalid.push(file.name); // Files that don't match the accepted types
        } else if (
          file.size <= urls.maxFileSize &&
          file.size > urls.minFileSize
        ) {
          validFiles.push(file);
        } else if (file.size > urls.maxFileSize) {
          exceeded.push(file.name);
        } else if (file.size < urls.minFileSize) {
            lesserSized.push(file.name);
       
        }
      });
      if (duplicate.length > 0) {
        dispatch(
          CloseOutActions.setErrorMessage(
            `The following files are already uploaded: ${duplicate.join(", ")}`
          )
        );
      } else if (invalid.length > 0) {
        dispatch(
          CloseOutActions.setErrorMessage(
            `The following files are invalid types: ${invalid.join(", ")}`
          )
        );
      } else if (exceeded.length > 0) {
        dispatch(
          CloseOutActions.setErrorMessage(
            `The following files exceed the maximum upload size of ${maxFileSizeInMB} MB: ${exceeded.join(
              ", "
            )}`
          )
        );
      } else if(lesserSized.length > 0) {
        dispatch(
          CloseOutActions.setErrorMessage(
            `The following files are smaller than the minimum upload size of ${(urls.minFileSize / (1024 * 1024)).toFixed(2)} MB: ${lesserSized.join(
              ", "
            )}`
          )
        );

      } else {
        dispatch(CloseOutActions.setErrorMessage(null));
      }

      if (validFiles.length > 0) {
        dispatch(CloseOutActions.addFiles(validFiles));
        dispatch(CloseOutActions.setisfileAdded(true));
        setTimeout(() => {
          endOfFilesRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  };
  const handleRemoveFile = (fileName: string) => {
    dispatch(CloseOutActions.handleremoveFile(fileName));
  };
  const handleConfidentialCheckboxChange = (event: any) => {
    dispatch(CloseOutActions.confidentialCheckBoxChange(event.target.checked));
  };

  // Handle checkbox change for Restricted
  const handleRestrictedCheckboxChange = (event: any) => {
    dispatch(CloseOutActions.restrictedCheckBoxChange(event.target.checked));
  };

  const handleInternalCheckboxChange = (event: any) => {
    dispatch(CloseOutActions.internalCheckBoxChange(event.target.checked));
  };
  // Handle Confidential radio button change
  const handleConfidentialRadioChange = (fileName: any, event: any) => {
    dispatch(
      CloseOutActions.confidentialRadioChange({
        value: event.target.value,
        fileName,
      })
    );
  };

  //handle file Download
  const handleDownloadFile = async (blob_file_name: string): Promise<void> => {
    try {
      setDownloadingFiles((prev) => new Set(prev).add(blob_file_name));
      const { url, fileName } = await downloadFile(blob_file_name, 2);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName; // Use the file name extracted from headers
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setDownloadingFiles((prev) => {
        const updatedSet = new Set(prev);
        updatedSet.delete(blob_file_name);
        return updatedSet;
      });
    }
  };
  useEffect(() => {
    if (downloadUrl) {
      const link = document.createElement("a");
      link.href = downloadUrl!;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadUrl(null); // Reset the download URL after opening
    }
  }, [downloadUrl]);

  // Handle Restricted radio button change
  const handleRestrictedRadioChange = (fileName: any, event: any) => {
    dispatch(
      CloseOutActions.restrictedRadioChange({
        value: event.target.value,
        fileName,
      })
    );
  };
  // Handle radio button change for Internal classification
  const handleInternalRadioChange = (fileName: any, event: any) => {
    dispatch(
      CloseOutActions.internalRadioChange({
        value: event.target.value,
        fileName,
      })
    );
  };
  return (
    <div>
      {/* Disclaimer Section */}
      {showUploadSection && (
        <>
          <div
            style={{
              margin: "10px",
              borderRadius: "10px",
              backgroundColor: "#fff3d1",
              padding: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <span
                style={{
                  fontSize: "18px",
                  marginRight: "10px",
                  color: "#FDAA53",
                }}
              >
                &#9888;
              </span>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  color: colors.secondary,
                  fontSize: "13px",
                  fontWeight: "bold",
                  display: "inline",
                }}
              >
                Disclaimer:
              </Typography>
            </div>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                color: colors.secondary,
                fontSize: "13px",
                marginLeft: "26px",
              }}
            >
              {parse(message)}
            </Typography>
          </div>

          {/* File Upload Section */}
          <div style={{ display: "flex", width: "100%" }}>
            <div style={{ width: "20%" }}></div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                width: "60%",
              }}
            >
              {/* Drag and Drop Upload Area */}
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                style={{
                  border: "2px dotted #669dd7",
                  padding: "20px",
                  margin: "10px 0",
                  borderRadius: "4px",
                  cursor: "pointer",
                  backgroundColor: "#eff5fc",
                  width: "90%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={FileFormat}
                    alt="File Upload Format"
                    style={{ width: "32px", height: "32px" }}
                  />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: "400",
                      fontFamily: '"Inter", sans-serif',
                      color: colors.secondary,
                      margin: "3px",
                    }}
                  >
                    Drag and drop your file(s) here to upload...
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      color: colors.secondary,
                      fontSize: "12px",
                      fontWeight: "bold",
                      display: "inline",
                    }}
                  >
                    Maximum upload file size: {maxFileSizeInMB}MB
                  </Typography>
                  {!isedit ? (
                    <>
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          color: colors.secondary,
                          fontSize: "10px",
                          display: "inline",
                        }}
                      >
                        (SOW Documents - .docx,.pdf)
                      </Typography>

                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          color: colors.secondary,
                          fontSize: "10px",
                          display: "inline",
                        }}
                      >
                        (Other Documents -
                        .doc,.docx,.pdf,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.json)
                      </Typography>
                    </>
                  ) : (
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        color: colors.secondary,
                        fontSize: "10px",
                        display: "inline",
                      }}
                    >
                      (.doc,.docx,.pdf,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.json)
                    </Typography>
                  )}
                </div>
              </div>
              <Button
                onClick={triggerFileInput}
                variant="secondary"
                style={{
                  width: "90%",
                  justifyContent: "center",
                }}
              >
                Select File(s) to Upload
              </Button>
            </div>
            <div style={{ width: "20%" }}></div>
          </div>

          {errorMessage && (
            <Typography sx={{ color: "red", fontSize: 11, marginTop: "5px" }}>
              {errorMessage}
            </Typography>
          )}
        </>
      )}
      {/* File List with Table */}
      {files.length > 0 && (
        <Paper
          sx={{
            width: "100%",
            overflow: "hidden",
            boxShadow: "none",
            marginTop: "25px",
          }}
        >
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{ padding: "5px", width: "40%" }}
                    align="left"
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: "12px",
                        fontWeight: "700",
                        color: colors.primary,
                      }}
                    >
                      Documents
                    </Typography>
                  </TableCell>
                  <TableCell
                    style={{ padding: "5px", width: "10%" }}
                    align="left"
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: "12px",
                        fontWeight: "700",
                        color: colors.primary,
                        display: "flex",
                      }}
                    >
                      <input
                        type="checkbox"
                        style={{
                          marginRight: "5px",
                          accentColor: "#1976d2",
                        }}
                        checked={internalChecked}
                        onChange={handleInternalCheckboxChange}
                      />
                      Internal
                    </Typography>
                  </TableCell>
                  <TableCell
                    style={{ padding: "5px", width: "10%" }}
                    align="left"
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: "12px",
                        fontWeight: "700",
                        color: colors.primary,
                        display: "flex",
                      }}
                    >
                      <input
                        type="checkbox"
                        style={{
                          accentColor: "#1976d2", // Controls the color of the tick icon
                          marginRight: "5px",
                        }}
                        checked={confidentialChecked}
                        onChange={handleConfidentialCheckboxChange}
                      />
                      Confidential
                    </Typography>
                  </TableCell>

                  <TableCell
                    style={{ padding: "5px", width: "10%" }}
                    align="left"
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: "12px",
                        fontWeight: "700",
                        color: colors.primary,
                        display: "flex",
                      }}
                    >
                      <input
                        type="checkbox"
                        style={{
                          marginRight: "5px",
                          accentColor: "#1976d2",
                        }}
                        checked={restrictedChecked}
                        onChange={handleRestrictedCheckboxChange}
                      />
                      Restricted
                    </Typography>
                  </TableCell>

                  <TableCell
                    style={{ padding: "5px", width: "10%" }}
                    align="left"
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: "12px",
                        fontWeight: "700",
                        color: colors.primary,
                      }}
                    >
                      Actions
                    </Typography>
                  </TableCell>
                  {isedit && (
                    <TableCell
                      style={{ padding: "5px", width: "10%" }}
                      align="left"
                    >
                      {files.some(
                        (file: any) =>
                          file.document_classification !==
                          documentclassification.restricted
                      ) && (
                        <Typography
                          sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: "12px",
                            fontWeight: "700",
                            color: colors.primary,
                          }}
                        >
                          Download
                        </Typography>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file: any, index: any) => (
                  <TableRow key={file.name}>
                    <TableCell style={{ padding: "5px" }}>
                      <span
                        style={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: "14px",
                          fontWeight: "400",
                          color: colors.primary,
                          wordBreak: "break-all",
                          whiteSpace: "normal",
                          display: "block",
                        }}
                      >
                        {`${index + 1}. ${file.name}`}
                      </span>
                      {/* Progress Bar */}
                      {!uploadedFilesSuccess.includes(file.name) &&
                        uploadProgress[file.name] !== undefined && (
                          <LinearProgress
                            variant="determinate"
                            value={uploadProgress[file.name] || 0}
                            sx={{
                              height: "7px",
                              marginTop: "5px",
                              borderRadius: "24px",
                              backgroundColor: "#dfdfdf",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor:
                                  uploadProgress[file.name] === 100
                                    ? "#008553"
                                    : "#005cbc",
                              },
                            }}
                          />
                        )}
                    </TableCell>
                    <TableCell sx={{ padding: "5px" }}>
                      <input
                        type="radio"
                        name={`internal-${file.name}`}
                        value="yes"
                        checked={
                          file.document_classification ===
                            documentclassification.internal ||
                          internalSelection[file.name] === "yes"
                        }
                        onChange={(e) =>
                          handleInternalRadioChange(file.name, e)
                        }
                        disabled={uploadedFilesSuccess.includes(file.name)}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: "5px" }}>
                      <input
                        type="radio"
                        style={{
                          accentColor: "#1976d2",
                        }}
                        name={`confidential-${file.name}`}
                        value="yes"
                        checked={
                          file.document_classification ===
                            documentclassification?.confidential ||
                          confidentialSelection[file.name] === "yes"
                        }
                        onChange={(e) =>
                          handleConfidentialRadioChange(file.name, e)
                        }
                        disabled={uploadedFilesSuccess.includes(file.name)}
                      />
                    </TableCell>

                    <TableCell style={{ padding: "5px" }}>
                      <input
                        type="radio"
                        style={{
                          accentColor: "#1976d2",
                        }}
                        name={`restricted-${file.name}`}
                        value="yes"
                        checked={
                          file.document_classification ===
                            documentclassification.restricted ||
                          restrictedSelection[file.name] === "yes"
                        }
                        onChange={(e) =>
                          handleRestrictedRadioChange(file.name, e)
                        }
                        disabled={uploadedFilesSuccess.includes(file.name)}
                      />
                    </TableCell>

                    <TableCell style={{ padding: "5px", paddingLeft: "0px" }}>
                      {!uploadedFilesSuccess.includes(file.name) ? (
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <img
                            src={Delete}
                            alt="Delete File"
                            style={{
                              width: "14px",
                              height: "18px",
                              cursor: isUploading ? "not-allowed" : "pointer",
                            }}
                            onClick={() =>
                              !isUploading && handleRemoveFile(file.name)
                            }
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            cursor: "default",
                          }}
                        >
                          <CheckCircleOutlineIcon
                            sx={{
                              color: "green",
                              fontSize: "16px",
                            }}
                          />
                        </div>
                      )}
                    </TableCell>
                    {isedit && (
                      <>
                        <TableCell
                          style={{
                            padding: "5px",
                            paddingLeft: "0px",
                          }}
                        >
                          {uploadedFilesSuccess.includes(file.name) &&
                            (file.document_classification ===
                              documentclassification.internal ||
                              file.document_classification ===
                                documentclassification.confidential) && (
                              <ButtonIcon
                                icon={"file_download"}
                                size="large"
                                onClick={
                                  () => handleDownloadFile(file.blob_file_name) // Call the download function here
                                }
                                style={{
                                  backgroundColor: "transparent",
                                  width: "auto",
                                  minWidth: "auto",
                                  paddingLeft: "0px",
                                }}
                                disabled={downloadingFiles.has(
                                  file.blob_file_name
                                )}
                              />
                            )}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        style={{ display: "none" }}
        multiple
        accept=".doc,.docx,.pdf,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.json"
      />
      <div ref={endOfFilesRef} />
    </div>
  );
};

export default DocumentUploadInput;
